import { getEnvironment } from "shared/utils/getEnvironment";
import { GTMEvents, GTMEventParams } from "./types";

type GTMEventOptions = {
  eventCallback?: () => void;
  eventTimeout?: number;
};

const runCallbackSafely = (callback?: () => void) => {
  if (!callback) return;

  try {
    callback();
  } catch (error) {
    console.warn("⚠️ GTM callback failed:", error);
  }
};

export const sendGTMEvent = <T extends GTMEvents>(
  eventName: T,
  eventData: GTMEventParams[T],
  options?: GTMEventOptions,
) => {
  try {
    const eventNameEnvironment = `${eventName}-${getEnvironment()}`;

    console.log("GTM: ", eventNameEnvironment, eventData);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventNameEnvironment,
        ...eventData,
        ...options,
      });
      return;
    }

    console.warn("⚠️ GTM not available: ", eventNameEnvironment, eventData);
    runCallbackSafely(options?.eventCallback);
  } catch (error) {
    console.warn("⚠️ GTM event failed:", eventName, error);
    runCallbackSafely(options?.eventCallback);
  }
};

export const sendGTMEventAndWait = <T extends GTMEvents>(
  eventName: T,
  eventData: GTMEventParams[T],
  timeout = 1000,
) =>
  new Promise<void>((resolve) => {
    let completed = false;
    let fallbackTimer: number | undefined;

    const finish = () => {
      if (completed) return;
      completed = true;
      if (fallbackTimer !== undefined) {
        window.clearTimeout(fallbackTimer);
      }
      resolve();
    };

    if (typeof window === "undefined") {
      sendGTMEvent(eventName, eventData, { eventCallback: finish });
      return;
    }

    fallbackTimer = window.setTimeout(finish, timeout);

    sendGTMEvent(eventName, eventData, {
      eventCallback: finish,
      eventTimeout: timeout,
    });
  });
